import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../../services/apiClient';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const PrimaryButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #27443a; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const IconButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  color: #9b1c1c;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #fee2e2; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  td:nth-child(1) {
    text-transform: uppercase;
  }

  td:nth-child(2) {
    text-transform: uppercase;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }

  @media (max-width: 720px) {
    min-width: 420px;

    th, td {
      padding: 12px 10px;
      font-size: 0.9rem;
    }
  }
`;

const Notice = styled.div`
  padding: 16px;
  color: #374151;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalCard = styled.div`
  width: 520px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  outline: none;
  font-size: 0.95rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid #eee;
`;

const SecondaryButton = styled.button`
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #f9fafb; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: #374151;
`;

const PageInfo = styled.div`
  font-size: 0.95rem;
`;

const IDIOMAS_ENDPOINT_CANDIDATES = ['/api/idiomas', '/idiomas', '/api/idioma', '/idioma'];

const isDevMode = import.meta.env?.DEV;

const Idiomas = () => {
  const [idiomas, setIdiomas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nomeNovoIdioma, setNomeNovoIdioma] = useState('');
  const [siglaNovoIdioma, setSiglaNovoIdioma] = useState('');
  const [saving, setSaving] = useState(false);
  const [idiomasEndpoint, setIdiomasEndpoint] = useState(IDIOMAS_ENDPOINT_CANDIDATES[0]);

  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);

  const debugEnabled = (() => {
    try {
      return Boolean(window?.localStorage?.getItem('idiomasDebug') === '1');
    } catch (_) {
      return false;
    }
  })();

  const toAbsoluteApiUrl = (path) => {
    const base = String(API_BASE || '').replace(/\/+$/, '');
    let p = String(path || '');
    if (!p.startsWith('/')) p = '/' + p;
    if (/\/api$/i.test(base) && /^\/api(\/|$)/i.test(p)) {
      p = p.replace(/^\/api/i, '');
    }
    return `${base}${p}`;
  };

  const endpointCandidates = isDevMode
    ? IDIOMAS_ENDPOINT_CANDIDATES.filter((p) => String(p).startsWith('/api/'))
    : IDIOMAS_ENDPOINT_CANDIDATES;

  const requestJson = async (path, options) => {
    const baseUrl = toAbsoluteApiUrl(path);

    const doFetch = async (url) => {
      const res = await fetch(url, {
        headers: {
          accept: 'application/json',
          'cache-control': 'no-cache',
          pragma: 'no-cache',
          ...(options?.headers || {})
        },
        cache: 'no-store',
        ...options
      });
      return res;
    };

    let res = await doFetch(baseUrl);
    if (res.status === 304) {
      const sep = baseUrl.includes('?') ? '&' : '?';
      res = await doFetch(`${baseUrl}${sep}__ts=${Date.now()}`);
    }

    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch (_) { data = null; }
    if (!res.ok) {
      const msg = data?.message || data?.erro || text || `Erro ${res.status}`;
      throw new Error(msg);
    }
    return data;
  };

  const extractIdiomas = (data) => {
    const list = Array.isArray(data?.Registros)
      ? data.Registros
      : (Array.isArray(data?.registros)
        ? data.registros
        : (Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data?.items)
            ? data.items
            : (Array.isArray(data) ? data : []))));

    return list
      .filter((i) => i && (i.codigoIDIOMA || i.nomeIDIOMA || i.id || i.codigo || i.nome))
      .map((i) => ({
        codigo: String(i.codigoIDIOMA ?? i.id ?? i.codigo ?? '').trim(),
        nome: i.nomeIDIOMA ?? i.nome ?? i.descricao ?? '',
        sigla: i.siglaIDIOMA
          ?? i.siglaIdioma
          ?? i.siglaidioma
          ?? i.sigla_idioma
          ?? i.SIGLA_IDIOMA
          ?? i.SIGLA
          ?? i.sigla
          ?? i.codigoSigla
          ?? i.abreviacao
          ?? i.abreviation
          ?? i.acronimo
          ?? i.acronym
          ?? i.languageCode
          ?? (i.nomeIDIOMA || i.nome || i.descricao || '').slice(0, 3).toUpperCase()
      }));
  };

  const fetchFirstNonEmptyIdiomas = async () => {
    let lastError = null;
    for (const path of endpointCandidates) {
      try {
        const data = await requestJson(path);
        const normalized = extractIdiomas(data);
        if (normalized.length) return { path, list: normalized };
        // Se o endpoint respondeu mas veio vazio, retorna ele mesmo assim (não é erro)
        return { path, list: [] };
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError || new Error('Erro ao carregar idiomas');
  };

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const { list, path } = await fetchFirstNonEmptyIdiomas();
      setIdiomas(list);
      if (path) setIdiomasEndpoint(path);
      if (debugEnabled) {
        console.log('[Idiomas] refresh', { path, count: list.length });
      }
      return { list, path };
    } catch (e) {
      setError(String(e?.message || e || 'Erro ao carregar idiomas'));
      return { list: [], path: IDIOMAS_ENDPOINT_CANDIDATES[0] };
    } finally {
      setLoading(false);
    }
  };

  const createIdioma = async (nome, sigla) => {
    const payload = {
      // Campos mínimos esperados pelo backend
      nome,
      nomeIDIOMA: nome,
      sigla,
      siglaIDIOMA: sigla,
      // Aliases extras evitados para não causar 400
    };
    const endpointCandidates = [
      ...(idiomasEndpoint ? [idiomasEndpoint] : []),
      ...IDIOMAS_ENDPOINT_CANDIDATES.filter((candidate) => candidate !== idiomasEndpoint)
    ];
    for (const path of endpointCandidates) {
      try {
        await requestJson(path, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload)
        });
        return;
      } catch (_) {
        if (debugEnabled) console.error('[Idiomas] createIdioma failed', path, _?.message || _);
        // tenta próximo
      }
    }
    throw new Error('Não foi possível criar idioma (endpoint não encontrado).');
  };

  const deleteIdioma = async (id) => {
    // Backend expõe DELETE /api/idiomas/:id usando codigoIDIOMA como chave
    const normalizedId = encodeURIComponent(String(id ?? '').trim());
    const endpoint = String(idiomasEndpoint || IDIOMAS_ENDPOINT_CANDIDATES[0]).replace(/\/+/, '/');
    const basePath = endpoint.replace(/\/+$/, '');
    const deletePath = `${basePath}/${normalizedId}`;
    const url = toAbsoluteApiUrl(deletePath);
    if (debugEnabled) {
      console.log('[Idiomas] DELETE', url);
    }
    const res = await fetch(url, {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
      },
    });

    const text = await res.text();
    if (debugEnabled) {
      console.log('[Idiomas] DELETE response', { status: res.status, body: text });
    }
    if (!res.ok) {
      throw new Error(text || `Erro ${res.status} ao excluir idioma`);
    }

    return { url, status: res.status, body: text };
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setActionError('');

    fetchFirstNonEmptyIdiomas()
      .then(({ list, path }) => {
        if (!active) return;
        setIdiomas(list);
        if (path) {
          setIdiomasEndpoint(path);
        }
      })
      .catch((e) => {
        if (!active) return;
        setError(String(e?.message || e || 'Erro ao carregar idiomas'));
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((idiomas?.length || 0) / PAGE_SIZE));
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [idiomas, PAGE_SIZE]);

  const openCreateModal = () => {
    setActionError('');
    setNomeNovoIdioma('');
    setSiglaNovoIdioma('');
    setIsModalOpen(true);
  };

  const closeCreateModal = () => {
    if (saving) return;
    setIsModalOpen(false);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    const nome = String(nomeNovoIdioma || '').trim();
    const sigla = String(siglaNovoIdioma || '').trim().toUpperCase();
    if (!nome) {
      setActionError('Preencha o nome do idioma.');
      return;
    }
    if (!sigla) {
      setActionError('Preencha a sigla do idioma.');
      return;
    }
    setSaving(true);
    setActionError('');
    try {
      await createIdioma(nome, sigla);
      setIsModalOpen(false);
      await refresh();
    } catch (err) {
      setActionError(String(err?.message || err || 'Erro ao criar idioma'));
    } finally {
      setSaving(false);
    }
  };

  const handleClickDelete = async (idioma) => {
    const id = idioma?.codigo; // aqui já é o codigoIDIOMA normalizado em extractIdiomas
    const label = idioma?.nome ? `${idioma.nome}` : String(id);

    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: `Excluir idioma ${label}? Esta ação não poderá ser desfeita!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    setActionError('');
    try {
      const del = await deleteIdioma(id);
      const { list } = await refresh();
      const stillExists = (list || []).some((item) => String(item.codigo) === String(id));
      if (stillExists) {
        const details = del
          ? `DELETE ${del.url}\nStatus: ${del.status}${del.body ? `\nBody: ${String(del.body).slice(0, 300)}` : ''}`
          : '';
        Swal.fire(
          'Atenção',
          `O idioma ainda está retornando no GET depois do DELETE.\n\n${details}\n\nPara habilitar logs no console: execute localStorage.setItem('idiomasDebug','1') e recarregue a página.`,
          'warning'
        );
        return;
      }
      Swal.fire('Excluído!', 'Idioma excluído com sucesso.', 'success');
    } catch (err) {
      setActionError(String(err?.message || err || 'Erro ao excluir idioma'));
      Swal.fire('Erro', 'Não foi possível excluir o idioma.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil((idiomas?.length || 0) / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const idiomasPage = (idiomas || []).slice(startIndex, endIndex);

  return (
    <PageContainer>
      <HeaderRow>
        <Title>Idiomas</Title>
        <ActionsRow>
          <PrimaryButton type="button" onClick={openCreateModal} disabled={saving}>
            <FaPlus />
            Adicionar
          </PrimaryButton>
        </ActionsRow>
      </HeaderRow>

      <TableWrapper>
        {loading && <Notice>Carregando...</Notice>}
        {!loading && error && <Notice>Erro: {error}</Notice>}
        {!loading && !error && actionError && <Notice>Erro: {actionError}</Notice>}
        {!loading && !error && idiomas.length === 0 && (
          <Notice>
            Nenhum idioma cadastrado. Verifique se a tabela <strong>tb_idiomas</strong> possui registros neste ambiente.
          </Notice>
        )}

        {!loading && !error && idiomas.length > 0 && (
          <>
            <Table>
              <thead>
                <tr>
                  <th>Sigla</th>
                  <th>Nome</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {idiomasPage.map((i) => (
                  <tr key={String(i.codigo)}>
                    <td>{i.sigla}</td>
                    <td>{i.nome}</td>
                    <td>
                      <IconButton type="button" onClick={() => handleClickDelete(i)} disabled={saving} aria-label={`Excluir ${i.nome || i.codigo}`}>
                        <FaTrashAlt />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {idiomas.length > PAGE_SIZE && (
              <PaginationRow>
                <SecondaryButton type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={saving || page <= 1}>
                  Anterior
                </SecondaryButton>
                <PageInfo>
                  Página {page} de {totalPages}
                </PageInfo>
                <SecondaryButton type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={saving || page >= totalPages}>
                  Próxima
                </SecondaryButton>
              </PaginationRow>
            )}
          </>
        )}
      </TableWrapper>

      {isModalOpen && (
        <ModalOverlay onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeCreateModal();
        }}>
          <ModalCard>
            <ModalHeader>
              <ModalTitle>Adicionar Idioma</ModalTitle>
              <SecondaryButton type="button" onClick={closeCreateModal} disabled={saving}>Fechar</SecondaryButton>
            </ModalHeader>

            <form onSubmit={handleSubmitCreate}>
              <ModalBody>
                <FieldLabel>Nome</FieldLabel>
                <TextInput value={nomeNovoIdioma} onChange={(e) => setNomeNovoIdioma(e.target.value)} placeholder="Ex: Português" />
                <FieldLabel style={{ marginTop: 12 }}>Sigla</FieldLabel>
                <TextInput value={siglaNovoIdioma} onChange={(e) => setSiglaNovoIdioma(e.target.value.toUpperCase())} placeholder="Ex: PT" maxLength={5} />
                {actionError && <Notice>Erro: {actionError}</Notice>}
              </ModalBody>
              <ModalActions>
                <SecondaryButton type="button" onClick={closeCreateModal} disabled={saving}>Cancelar</SecondaryButton>
                <PrimaryButton type="submit" disabled={saving}>Salvar</PrimaryButton>
              </ModalActions>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default Idiomas;
